import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import PageConsensusBanner from '../components/PageConsensusBanner';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  Star, ArrowLeft, CircleNotch, DownloadSimple, CaretUp, CaretDown,
  ArrowsOutLineHorizontal, TrendUp, TrendDown, Fire, Snowflake, Target,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { MyPredictionBlock, syncConstraintsToDB } from '../components/MyPrediction';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ALL_POSITIONS = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5', 'lucky_star_1', 'lucky_star_2'];
const POS_LABELS = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5', lucky_star_1: 'LS1', lucky_star_2: 'LS2' };
const MAX_PICKS = 10;

const TrendBadge = ({ trend }) => {
  if (trend === 'increasing') return <span className="inline-flex items-center gap-0.5 text-green-700 bg-green-100 rounded px-1.5 py-0.5 text-[10px] font-bold"><TrendUp size={10} />UP</span>;
  if (trend === 'decreasing') return <span className="inline-flex items-center gap-0.5 text-red-700 bg-red-100 rounded px-1.5 py-0.5 text-[10px] font-bold"><TrendDown size={10} />DN</span>;
  if (trend === 'hot') return <span className="inline-flex items-center gap-0.5 text-orange-700 bg-orange-100 rounded px-1.5 py-0.5 text-[10px] font-bold"><Fire size={10} />HOT</span>;
  if (trend === 'cold') return <span className="inline-flex items-center gap-0.5 text-blue-700 bg-blue-100 rounded px-1.5 py-0.5 text-[10px] font-bold"><Snowflake size={10} />COLD</span>;
  return <span className="text-gray-400 text-[10px]">-</span>;
};

const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const handleMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    startXRef.current = e.clientX; startWidthRef.current = width;
    const move = (ev) => { onResize(columnKey, Math.max(35, startWidthRef.current + (ev.clientX - startXRef.current))); };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  return (
    <th className={`relative ${className}`} style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }} onClick={onClick}>
      <div className="flex items-center justify-center gap-1 pr-2 font-bold">{children}</div>
      <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center" onMouseDown={handleMouseDown} onClick={(e) => e.stopPropagation()}>
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

const P_CELL = (score) => {
  if (score == null) return { cls: 'bg-slate-100 text-slate-500', label: '' };
  const s = Math.round(score);
  const abs = Math.abs(s);
  const sign = s > 0 ? '+' : s < 0 ? '-' : '';
  const label = `${sign}${abs}`;
  if (s >= 60) return { cls: 'bg-emerald-600 text-white font-black', label, tag: 'HOT' };
  if (s >= 30) return { cls: 'bg-emerald-300 text-emerald-900 font-bold', label };
  if (s >= 10) return { cls: 'bg-emerald-100 text-emerald-800', label };
  if (s > -10) return { cls: 'bg-slate-100 text-slate-600', label };
  if (s > -30) return { cls: 'bg-rose-100 text-rose-800', label };
  if (s > -60) return { cls: 'bg-rose-300 text-rose-900 font-bold', label };
  return { cls: 'bg-rose-600 text-white font-black', label, tag: 'COLD' };
};

const DIFF_BADGE = (diff) => {
  if (diff == null) return { cls: 'text-slate-400', txt: '0.00%' };
  const v = Number(diff).toFixed(2);
  if (diff > 1) return { cls: 'text-emerald-700 font-bold', txt: `+${v}%` };
  if (diff > 0) return { cls: 'text-emerald-600', txt: `+${v}%` };
  if (diff < -1) return { cls: 'text-rose-700 font-bold', txt: `${v}%` };
  if (diff < 0) return { cls: 'text-rose-600', txt: `${v}%` };
  return { cls: 'text-slate-500', txt: `${v}%` };
};

// Hoisted so BallTable / ScoringMatrix aren't creating a new component type
// on every render (was destroying the whole table subtree per re-render).
const SortIndicator = ({ active, dir }) => {
  if (!active) return null;
  return dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
};

const BallTable = React.memo(({ numbersData, sortConfig, onSort, ballIdx, monthAbbr }) => {
  const tableRef = useRef(null);
  const topRef = useRef(null);
  // Synchronize the top "movement bar" with the actual table scroll. Bi-directional.
  useEffect(() => {
    const top = topRef.current;
    const bottom = tableRef.current;
    if (!top || !bottom) return;
    let syncing = false;
    const onTop = () => {
      if (syncing) return;
      syncing = true;
      bottom.scrollLeft = top.scrollLeft;
      syncing = false;
    };
    const onBottom = () => {
      if (syncing) return;
      syncing = true;
      top.scrollLeft = bottom.scrollLeft;
      syncing = false;
    };
    top.addEventListener('scroll', onTop);
    bottom.addEventListener('scroll', onBottom);
    return () => {
      top.removeEventListener('scroll', onTop);
      bottom.removeEventListener('scroll', onBottom);
    };
  }, []);

  const sorted = useMemo(() => {
    return [...numbersData].sort((a, b) => {
    const k = sortConfig.key;
    let aVal = 0, bVal = 0;
    if (k === 'number') { aVal = a.number; bVal = b.number; }
    else if (k === 'v2_param1') { aVal = a.v2_param1 || 0; bVal = b.v2_param1 || 0; }
    else if (k === 'v2_param2') { aVal = a.v2_param2 || 0; bVal = b.v2_param2 || 0; }
    else if (k === 'v2_may_last4_count') { aVal = a.v2_may_last4_count || 0; bVal = b.v2_may_last4_count || 0; }
    else if (k === 'v2_may_last4_pct') { aVal = a.v2_may_last4_pct || 0; bVal = b.v2_may_last4_pct || 0; }
    else if (k === 'v2_may_other_balls_count') { aVal = a.v2_may_other_balls_count || 0; bVal = b.v2_may_other_balls_count || 0; }
    else if (k === 'v2_may_other_balls_pct') { aVal = a.v2_may_other_balls_pct || 0; bVal = b.v2_may_other_balls_pct || 0; }
    else if (k === 'v2_param3') { aVal = a.v2_param3 || 0; bVal = b.v2_param3 || 0; }
    else if (k === 'v2_param5') { aVal = a.v2_param5 || 0; bVal = b.v2_param5 || 0; }
    else if (k === 'recent_25') { aVal = a.recent_25 || 0; bVal = b.recent_25 || 0; }
    else if (k === 'recent_50') { aVal = a.recent_50 || 0; bVal = b.recent_50 || 0; }
    else if (k === 'max_r25_ever') { aVal = a.max_r25_ever || 0; bVal = b.max_r25_ever || 0; }
    else if (k === 'max_r50_ever') { aVal = a.max_r50_ever || 0; bVal = b.max_r50_ever || 0; }
    else if (k === 'avg_r25_ever') { aVal = a.avg_r25_ever || 0; bVal = b.avg_r25_ever || 0; }
    else if (k === 'avg_r50_ever') { aVal = a.avg_r50_ever || 0; bVal = b.avg_r50_ever || 0; }
    else if (k === 'gap_overdue_ratio') {
      aVal = a.v2_avg_gap > 0 ? a.v2_current_gap_ball / a.v2_avg_gap : 0;
      bVal = b.v2_avg_gap > 0 ? b.v2_current_gap_ball / b.v2_avg_gap : 0;
    }
    else if (k === 'param_total') {
      aVal = (a.v2_param1 || 0) + (a.v2_param2 || 0) + (a.v2_param3 || 0) + (a.v2_param4 || 0) + (a.v2_param5 || 0);
      bVal = (b.v2_param1 || 0) + (b.v2_param2 || 0) + (b.v2_param3 || 0) + (b.v2_param4 || 0) + (b.v2_param5 || 0);
    } else if (a[k] != null) { aVal = a[k] || 0; bVal = b[k] || 0; }
    return sortConfig.dir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [numbersData, sortConfig]);

  const renderSortIcon = (k) => sortConfig.key !== k ? null :
    (sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />);

  const hCls = "px-2 py-2 text-[10px] uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-gray-200 text-center whitespace-nowrap";
  const cCls = "px-2 py-1.5 text-center border-b border-gray-100 font-mono text-[11px]";

  const monthLabel = monthAbbr || 'Month';

  // Group-header row — column groups (P1..P5 + Extended intentionally removed per user request)
  const groupHeaders = [
    { label: 'Identity', cls: 'bg-slate-200 text-slate-800', span: 1 },
    { label: 'Historical (pre-2026)', cls: 'bg-blue-100 text-blue-800', span: 3 },
    { label: '2026 Performance', cls: 'bg-emerald-100 text-emerald-800', span: 3 },
    { label: `${monthLabel} Affinity`, cls: 'bg-amber-100 text-amber-800', span: 3 },
    { label: `${monthLabel} @ Other Balls (till 2025)`, cls: 'bg-sky-100 text-sky-800', span: 2 },
    { label: `${monthLabel} Affinity — Last 4 Yrs`, cls: 'bg-lime-100 text-lime-800', span: 2 },
    { label: 'Gap Analysis', cls: 'bg-purple-100 text-purple-800', span: 3 },
    { label: 'Recent Momentum (any position)', cls: 'bg-cyan-100 text-cyan-800', span: 6 },
    { label: 'Affinity to Date & Month (all 7 balls)', cls: 'bg-fuchsia-100 text-fuchsia-800', span: 1 },
  ];

  return (
    <div>
      {/* Top "movement bar" — synchronized horizontal scrollbar mirroring the
          table below. Lets users navigate the wide 25-column grid without
          first scrolling down to the native bar at the bottom. */}
      <div
        ref={topRef}
        className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 mb-1"
        style={{ height: '16px' }}
        data-testid={`w6-top-scroll-ball-${ballIdx}`}
      >
        <div style={{ width: '1500px', height: '1px' }} />
      </div>
      <div ref={tableRef} className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs bg-white" style={{ minWidth: '1500px' }} data-testid={`w6-v2-table-ball-${ballIdx}`}>
        <thead className="sticky top-0 z-10">
          {/* Group header row */}
          <tr>
            {groupHeaders.map((g, i) => (
              <th key={i} colSpan={g.span} className={`${g.cls} text-[10px] px-2 py-1.5 text-center border-b-2 border-slate-300 uppercase tracking-wider`}>
                {g.label}
              </th>
            ))}
          </tr>
          {/* Column header row */}
          <tr className="bg-gray-100">
            <th className={`${hCls} bg-slate-200`} onClick={() => onSort('number')}>
              # {renderSortIcon("number")}
            </th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('v2_times_till_2025')} title="Total appearances at this ball up to end of 2025">
              Times till 2025 {renderSortIcon("v2_times_till_2025")}
            </th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('v2_avg_py')} title="Average per year (pre-2026)">
              Avg/Yr {renderSortIcon("v2_avg_py")}
            </th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('v2_pct_pre_2026')} title="% frequency pre-2026">
              % {renderSortIcon("v2_pct_pre_2026")}
            </th>
            <th className={`${hCls} bg-emerald-50`} onClick={() => onSort('v2_cy_ball')} title="Appearances in 2026 at this ball">
              2026 {renderSortIcon("v2_cy_ball")}
            </th>
            <th className={`${hCls} bg-emerald-50`} onClick={() => onSort('v2_pct_2026')} title="% frequency in 2026">
              % {renderSortIcon("v2_pct_2026")}
            </th>
            <th className={`${hCls} bg-emerald-50`} onClick={() => onSort('v2_diff_pct')} title="2026 % minus Pre-2026 %">
              Diff {renderSortIcon("v2_diff_pct")}
            </th>
            <th className={`${hCls} bg-amber-50`} onClick={() => onSort('v2_may_hist_total')} title={`Total times this number appeared at this ball in ${monthLabel} historically (pre-2026)`}>
              {monthLabel}&apos;til25 {renderSortIcon("v2_may_hist_total")}
            </th>
            <th className={`${hCls} bg-amber-50`} onClick={() => onSort('v2_may_pct')} title={`% of ${monthLabel} draws this number appeared`}>
              {monthLabel}% {renderSortIcon("v2_may_pct")}
            </th>
            <th className={`${hCls} bg-amber-50`} onClick={() => onSort('v2_may_diff')} title={`${monthLabel}% minus Overall% (positive = ${monthLabel} favours)`}>
              %Diff {renderSortIcon("v2_may_diff")}
            </th>
            <th className={`${hCls} bg-sky-50`} onClick={() => onSort('v2_may_other_balls_count')} title={`Total times this number appeared in ${monthLabel} at any of the OTHER 4 ball positions (pre-2026, full history)`}>
              @Other {renderSortIcon("v2_may_other_balls_count")}
            </th>
            <th className={`${hCls} bg-sky-50`} onClick={() => onSort('v2_may_other_balls_pct')} title={`% per-slot: count / (${monthLabel} pre-2026 draws × 4 other balls)`}>
              @Other % {renderSortIcon("v2_may_other_balls_pct")}
            </th>
            <th className={`${hCls} bg-lime-50`} onClick={() => onSort('v2_may_last4_count')} title={`Times this number appeared at this ball in ${monthLabel} during the last 4 years (2022-2025)`}>
              L4 {monthLabel} {renderSortIcon("v2_may_last4_count")}
            </th>
            <th className={`${hCls} bg-lime-50`} onClick={() => onSort('v2_may_last4_pct')} title={`% of the last 4 years' ${monthLabel} draws that hit this number at this ball`}>
              L4 % {renderSortIcon("v2_may_last4_pct")}
            </th>
            <th className={`${hCls} bg-purple-50`} onClick={() => onSort('v2_max_gap_ever')} title="Longest ever gap between appearances at this ball position (in draws)">
              Max {renderSortIcon("v2_max_gap_ever")}
            </th>
            <th className={`${hCls} bg-purple-50`} onClick={() => onSort('v2_avg_gap')} title="Average rebound gap (mean of all historical gaps at this ball)">
              Avg {renderSortIcon("v2_avg_gap")}
            </th>
            <th className={`${hCls} bg-purple-50`} onClick={() => onSort('v2_current_gap_ball')} title="Current gap since last appearance at THIS ball (in draws)">
              Cur {renderSortIcon("v2_current_gap_ball")}
            </th>
            <th className={`${hCls} bg-cyan-50 text-cyan-800 border-l border-slate-300`} onClick={() => onSort('max_r25_ever')} title="Historical ceiling — the largest any-position count any single number ever hit within a 5-draw window across all pre-2026 draws for this number. Compare with R25 to see if the current reading is close to the historical maximum (exhausted) or still has room to run.">
              Max R25 {renderSortIcon("max_r25_ever")}
            </th>
            <th className={`${hCls} bg-cyan-50 text-cyan-700 italic`} onClick={() => onSort('avg_r25_ever')} title="Average number of any-position appearances per 5-draw window across all pre-2026 history. = 5 × total_appearances / total_pre_2026_draws. Represents the 'baseline' rate — R25 above this is genuinely hot, below is quiet.">
              Avg R25 {renderSortIcon("avg_r25_ever")}
            </th>
            <th className={`${hCls} bg-cyan-50`} onClick={() => onSort('recent_25')} title="R25 = count of this number appearing at ANY ball position in the last 5 draws (25 drawn numbers total)">
              R25 {renderSortIcon("recent_25")}
            </th>
            <th className={`${hCls} bg-cyan-50 text-cyan-800 border-l border-slate-300`} onClick={() => onSort('max_r50_ever')} title="Historical ceiling — largest any-position count this number ever hit within a 10-draw window across all pre-2026 draws.">
              Max R50 {renderSortIcon("max_r50_ever")}
            </th>
            <th className={`${hCls} bg-cyan-50 text-cyan-700 italic`} onClick={() => onSort('avg_r50_ever')} title="Average number of any-position appearances per 10-draw window across all pre-2026 history. = 10 × total_appearances / total_pre_2026_draws.">
              Avg R50 {renderSortIcon("avg_r50_ever")}
            </th>
            <th className={`${hCls} bg-cyan-50`} onClick={() => onSort('recent_50')} title="R50 = count of this number appearing at ANY ball position in the last 10 draws (50 drawn numbers total)">
              R50 {renderSortIcon("recent_50")}
            </th>
            <th className={`${hCls} bg-fuchsia-50 text-fuchsia-900 border-l border-slate-300`} onClick={() => onSort('v2_same_date_count')} title="Same-Date Affinity: how many times this number has appeared across ALL 7 positions (Ball 1-5 + LS 1-2) in every historical draw that fell on the SAME day-of-month AND SAME month as the NEXT predicted draw. Updates automatically each time the next-draw date changes.">
              Same-Date {renderSortIcon("v2_same_date_count")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            // Σ Total column removed per user request — window is now purely
            // descriptive (raw historical + current-month + recent-momentum stats).
            const d = DIFF_BADGE(row.v2_diff_pct);
            const md = DIFF_BADGE(row.v2_may_diff);
            return (
              <tr key={row.number} className="hover:bg-yellow-50 transition-colors" data-testid={`w6-v2-row-${ballIdx}-${row.number}`}>
                <td className={`${cCls} font-black text-slate-900 bg-slate-50`}>{row.number}</td>
                <td className={cCls}>{row.v2_times_till_2025}</td>
                <td className={cCls}>{row.v2_avg_py}</td>
                <td className={cCls}>{row.v2_pct_pre_2026.toFixed(2)}%</td>
                <td className={`${cCls} ${row.v2_cy_ball === 0 ? 'text-slate-300' : 'font-bold text-emerald-700'}`}>{row.v2_cy_ball}</td>
                <td className={cCls}>{row.v2_pct_2026.toFixed(2)}%</td>
                <td className={`${cCls} ${d.cls}`}>{d.txt}</td>
                <td className={cCls}>{row.v2_may_hist_total}</td>
                <td className={cCls}>{row.v2_may_pct.toFixed(2)}%</td>
                <td className={`${cCls} ${md.cls}`}>{md.txt}</td>
                <td className={`${cCls} ${(row.v2_may_other_balls_count || 0) >= 20 ? 'font-bold text-sky-700' : 'text-slate-600'}`} data-testid={`w6-v2-other-count-${ballIdx}-${row.number}`}>
                  {row.v2_may_other_balls_count ?? 0}
                </td>
                <td className={cCls} data-testid={`w6-v2-other-pct-${ballIdx}-${row.number}`}>
                  {(row.v2_may_other_balls_pct ?? 0).toFixed(2)}%
                </td>
                <td className={`${cCls} ${(row.v2_may_last4_count || 0) >= 3 ? 'font-bold text-lime-700' : 'text-slate-600'}`} data-testid={`w6-v2-l4count-${ballIdx}-${row.number}`}>
                  {row.v2_may_last4_count ?? 0}
                </td>
                <td className={cCls} data-testid={`w6-v2-l4pct-${ballIdx}-${row.number}`}>
                  {(row.v2_may_last4_pct ?? 0).toFixed(2)}%
                </td>
                <td className={cCls}>{row.v2_max_gap_ever}</td>
                <td className={cCls}>{row.v2_avg_gap}</td>
                <td className={cCls}>{row.v2_current_gap_ball}</td>
                <td className={`${cCls} font-bold text-cyan-800 border-l border-slate-200`} data-testid={`w6-v2-maxr25-${ballIdx}-${row.number}`}>
                  {row.max_r25_ever ?? '-'}
                </td>
                <td className={`${cCls} text-cyan-700 italic`} data-testid={`w6-v2-avgr25-${ballIdx}-${row.number}`}>
                  {row.avg_r25_ever != null ? row.avg_r25_ever.toFixed(2) : '-'}
                </td>
                <td className={`${cCls} ${row.recent_25 === 0 ? 'text-rose-300' : row.recent_25 >= 2 ? 'font-bold text-emerald-700' : 'text-slate-600'} ${row.max_r25_ever && row.recent_25 >= row.max_r25_ever ? 'ring-2 ring-emerald-400 rounded' : ''}`} data-testid={`w6-v2-r25-${ballIdx}-${row.number}`} title={`Any-position appearances in the last 5 draws (25 slots). Historical max = ${row.max_r25_ever ?? '?'}.`}>
                  {row.recent_25 ?? 0}
                </td>
                <td className={`${cCls} font-bold text-cyan-800 border-l border-slate-200`} data-testid={`w6-v2-maxr50-${ballIdx}-${row.number}`}>
                  {row.max_r50_ever ?? '-'}
                </td>
                <td className={`${cCls} text-cyan-700 italic`} data-testid={`w6-v2-avgr50-${ballIdx}-${row.number}`}>
                  {row.avg_r50_ever != null ? row.avg_r50_ever.toFixed(2) : '-'}
                </td>
                <td className={`${cCls} ${row.recent_50 === 0 ? 'text-rose-300' : row.recent_50 >= 3 ? 'font-bold text-emerald-700' : 'text-slate-600'} ${row.max_r50_ever && row.recent_50 >= row.max_r50_ever ? 'ring-2 ring-emerald-400 rounded' : ''}`} data-testid={`w6-v2-r50-${ballIdx}-${row.number}`} title={`Any-position appearances in the last 10 draws (50 slots). Historical max = ${row.max_r50_ever ?? '?'}.`}>
                  {row.recent_50 ?? 0}
                </td>
                <td className={`${cCls} border-l border-slate-200 ${(row.v2_same_date_count || 0) >= 3 ? 'bg-fuchsia-100 font-black text-fuchsia-900' : (row.v2_same_date_count || 0) >= 1 ? 'bg-fuchsia-50 font-bold text-fuchsia-800' : 'text-slate-300'}`} data-testid={`w6-same-date-${ballIdx}-${row.number}`} title={`Number ${row.number} appeared ${row.v2_same_date_count ?? 0} time(s) across all 7 positions in historical draws matching the next-draw date.`}>
                  {row.v2_same_date_count ?? 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
});
BallTable.displayName = 'BallTable';

/**
 * Years Above +3% Diff — last 10 completed years + the in-progress current
 * year, per-ball count of how many numbers (1-50) had that YEAR's FULL-YEAR
 * % (all 12 months) at this ball exceed the historical pre-2026 baseline %
 * by more than +3 percentage points. Same formula/scope as the "2026
 * Performance -> Diff" column (the current-year cell IS that column's
 * count), generalized to each of the last 10 completed years too.
 */
const YearOverdueSummary = ({ summary, years, currentYear, ballIdx }) => {
  if (!summary || !years?.length) return null;
  const allYears = currentYear ? [...years, currentYear] : years;
  const maxCount = Math.max(...allYears.map((y) => summary[String(y)] ?? 0));
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3" data-testid={`w6-year-overdue-summary-ball-${ballIdx}`}>
      <h4 className="text-xs font-black text-gray-700 uppercase tracking-wide mb-1">
        Years Above +3% Diff
      </h4>
      <p className="text-[11px] text-gray-500 mb-3">
        Last {years.length} completed years (+ {currentYear} year-to-date): count of numbers whose full-year % that year beat their historical (pre-2026) % by more than +3 points — same formula as the 2026 Performance → Diff column above, applied to each past year.
      </p>
      <div className="grid grid-cols-5 sm:grid-cols-11 gap-2">
        {allYears.map((yr) => {
          const count = summary[String(yr)] ?? 0;
          const isCurrent = yr === currentYear;
          return (
            <div
              key={yr}
              className={`rounded-lg px-2 py-2 text-center ${isCurrent ? 'bg-sky-50 border border-sky-300' : count === maxCount && count > 0 ? 'bg-rose-100 border border-rose-300' : 'bg-gray-50 border border-gray-100'}`}
              data-testid={`w6-year-overdue-${ballIdx}-${yr}`}
            >
              <div className="text-[10px] text-gray-500 font-mono">{yr}{isCurrent ? ' (YTD)' : ''}</div>
              <div className={`text-sm font-black font-mono ${isCurrent ? 'text-sky-700' : count === maxCount && count > 0 ? 'text-rose-600' : 'text-gray-800'}`}>{count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


/**
 * Scoring Matrix — user-defined per-number, per-ball composite score.
 *
 * Formula per (number, ball):
 *   score = hist_pct + diff_2026 + jul_diff + jul_other_balls_pct + jul_last4_pct + gap_ratio + (same_date_affinity × 2) + (r25_momentum × 2)
 *
 * Where:
 *   hist_pct            = v2_pct_pre_2026
 *   diff_2026           = v2_diff_pct
 *   jul_diff            = v2_may_diff
 *   jul_other_balls_pct = v2_may_other_balls_pct
 *   jul_last4_pct       = v2_may_last4_pct
 *   gap_ratio           = v2_current_gap_ball / v2_avg_gap   (0 when avg_gap = 0)
 *   same_date_affinity  = v2_same_date_count × 2             (same across all 5 balls — adds count × 2 × 5 to Σ Total)
 *   r25_momentum        = recent_25 × 2                      (any-position hits in last 5 draws; same across all 5 balls)
 *
 * Rows = numbers 1..50, columns = B1..B5 + Σ Total (sum across all 5 balls).
 * The "sum of the sum" is the Σ Total column — for each number, aggregates
 * how much composite affinity that number carries across the entire ball line-up.
 */
const ScoringMatrix = React.memo(({ data }) => {
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const monthLabel = data?.current_month_abbr || data?.current_month_name || 'Month';
  const [sortConfig, setSortConfig] = useState({ key: 'total', dir: 'desc' });

  // The per-ball row + score computation is expensive (50 numbers × 5 balls,
  // each with 8 additive components). Memoise so it only re-runs when the
  // upstream payload (`data`) actually changes — sort clicks now bypass this
  // 250-cell rebuild and only re-sort the tiny 50-row array.
  const rows = useMemo(() => {
    const perBallLookup = ballKeys.map(bk => {
      const nums = data?.balls?.[bk]?.numbers || [];
      return { bk, map: new Map(nums.map(n => [n.number, n])) };
    });
    const scoreFor = (row) => {
      if (!row) return { score: 0, gap: 0, hist: 0, diff2026: 0, julDiff: 0, julOther: 0, julL4: 0, sameDate: 0, r25: 0 };
      const hist = row.v2_pct_pre_2026 || 0;
      const diff2026 = row.v2_diff_pct || 0;
      const julDiff = row.v2_may_diff || 0;
      const julOther = row.v2_may_other_balls_pct || 0;
      const julL4 = row.v2_may_last4_pct || 0;
      const gap = row.v2_avg_gap > 0 ? (row.v2_current_gap_ball / row.v2_avg_gap) : 0;
      const sameDate = row.v2_same_date_count || 0;
      const r25 = row.recent_25 || 0;
      return {
        hist, diff2026, julDiff, julOther, julL4, gap, sameDate, r25,
        score: hist + diff2026 + julDiff + julOther + julL4 + gap + (sameDate * 2) + (r25 * 2),
      };
    };
    const out = [];
    for (let n = 1; n <= 50; n += 1) {
      const perBall = perBallLookup.map(({ bk, map }) => {
        const s = scoreFor(map.get(n));
        return { bk, ...s };
      });
      const total = perBall.reduce((acc, b) => acc + b.score, 0);
      out.push({ number: n, perBall, total });
    }
    return out;
  }, [data]);

  // Sort — cheap when `rows` is already memoised.
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'number') { aVal = a.number; bVal = b.number; }
      else if (sortConfig.key === 'total') { aVal = a.total; bVal = b.total; }
      else if (sortConfig.key.startsWith('ball_')) {
        const idx = parseInt(sortConfig.key.slice(-1), 10) - 1;
        aVal = a.perBall[idx].score; bVal = b.perBall[idx].score;
      }
      return sortConfig.dir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [rows, sortConfig]);

  const handleSort = (k) => {
    setSortConfig(prev => ({
      key: k,
      dir: prev.key === k && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const renderSortIcon2 = (k) => {
    if (sortConfig.key !== k) return <span className="text-slate-300 text-[9px] ml-1">↕</span>;
    return <span className="text-slate-800 text-[9px] ml-1">{sortConfig.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Per-ball column totals across all 50 numbers (footer row)
  const columnTotals = ballKeys.map((_, idx) => sortedRows.reduce((acc, r) => acc + r.perBall[idx].score, 0));
  const grandTotal = columnTotals.reduce((a, b) => a + b, 0);

  const cellColor = (score, ballIdx) => {
    // Colour bands — thresholds tuned so B1's stronger scores stand out
    // while B4/B5 (which naturally have lower components) still get differentiation.
    // ballIdx unused for now — kept for future per-ball palette tuning.
    void ballIdx;
    if (score >= 25) return 'bg-emerald-600 text-white font-black';
    if (score >= 18) return 'bg-emerald-300 text-emerald-900 font-bold';
    if (score >= 12) return 'bg-emerald-100 text-emerald-800';
    if (score >= 6)  return 'bg-slate-100 text-slate-700';
    if (score >= 2)  return 'bg-slate-50 text-slate-500';
    return 'text-slate-300';
  };

  return (
    <div className="space-y-4" data-testid="w6-scoring-matrix">
      <section className="bg-white border-2 border-amber-400 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-5 py-3 text-white">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-extrabold text-base sm:text-lg">🏏 Scoring Matrix — {monthLabel} {data?.current_year || ''}</h3>
              <p className="text-[11px] text-white/85 font-mono mt-1">
                Score = Historical% + 2026 %Diff + {monthLabel} %Diff + {monthLabel} @Other% + {monthLabel} L4% + (Cur Gap / Avg Gap) + (Same-Date Affinity × 2) + (R25 Momentum × 2)
              </p>
            </div>
            <div className="text-[11px] text-amber-100">
              Grand Total (all balls, all numbers) = <b>{grandTotal.toFixed(2)}</b>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-xs bg-white" data-testid="w6-scoring-matrix-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-amber-50 border-b border-amber-200">
                <th className="px-3 py-2 text-left font-black text-slate-800 cursor-pointer select-none" onClick={() => handleSort('number')}>
                  # {renderSortIcon2("number")}
                </th>
                {ballKeys.map((bk, i) => (
                  <th key={bk} className="px-3 py-2 text-center font-black text-slate-800 cursor-pointer select-none" onClick={() => handleSort(bk)}>
                    B{i + 1} {renderSortIcon2(bk)}
                  </th>
                ))}
                <th className="px-3 py-2 text-center font-black text-white bg-violet-600 cursor-pointer select-none" onClick={() => handleSort('total')}>
                  Σ Total {renderSortIcon2("total")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.number} className="hover:bg-yellow-50 border-b border-slate-100" data-testid={`w6-score-row-${row.number}`}>
                  <td className="px-3 py-1.5 font-black text-slate-900 bg-slate-50 border-r border-slate-200">
                    {row.number}
                  </td>
                  {row.perBall.map(({ bk, score, hist, diff2026, julDiff, julOther, julL4, gap, sameDate, r25 }, i) => (
                    <td
                      key={bk}
                      className={`px-3 py-1.5 text-center ${cellColor(score, i)}`}
                      data-testid={`w6-score-cell-${row.number}-${bk}`}
                      title={`Ball ${i + 1} · Number ${row.number}\n` +
                        `Historical %      : ${hist.toFixed(2)}\n` +
                        `2026 %Diff        : ${diff2026 >= 0 ? '+' : ''}${diff2026.toFixed(2)}\n` +
                        `${monthLabel} %Diff       : ${julDiff >= 0 ? '+' : ''}${julDiff.toFixed(2)}\n` +
                        `${monthLabel} @Other %    : ${julOther.toFixed(2)}\n` +
                        `${monthLabel} L4 %        : ${julL4.toFixed(2)}\n` +
                        `Gap ratio         : ${gap.toFixed(2)}\n` +
                        `Same-Date ×2      : +${((sameDate || 0) * 2).toFixed(0)} (count ${(sameDate || 0).toFixed(0)})\n` +
                        `R25 Momentum ×2   : +${((r25 || 0) * 2).toFixed(0)} (count ${(r25 || 0).toFixed(0)})\n` +
                        `SUM               : ${score.toFixed(2)}`}
                    >
                      {score.toFixed(2)}
                    </td>
                  ))}
                  <td
                    className="px-3 py-1.5 text-center bg-violet-100 text-violet-900 font-black border-l border-slate-200"
                    data-testid={`w6-score-total-${row.number}`}
                  >
                    {row.total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-amber-100 border-t-2 border-amber-500 font-black">
                <td className="px-3 py-2 text-slate-800">Σ per ball</td>
                {columnTotals.map((t, i) => (
                  <td key={i} className="px-3 py-2 text-center text-slate-800">{t.toFixed(2)}</td>
                ))}
                <td className="px-3 py-2 text-center text-violet-900 bg-violet-200">{grandTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
      <p className="text-[11px] text-slate-500 italic">
        Hover any cell to see the component-by-component breakdown.
        Colour bands: <span className="inline-block px-2 rounded bg-emerald-600 text-white">≥25</span>
        <span className="inline-block px-2 rounded bg-emerald-300 text-emerald-900 ml-1">≥18</span>
        <span className="inline-block px-2 rounded bg-emerald-100 text-emerald-800 ml-1">≥12</span>
        <span className="inline-block px-2 rounded bg-slate-100 text-slate-700 ml-1">≥6</span>
        <span className="inline-block px-2 rounded bg-slate-50 text-slate-500 ml-1">≥2</span>
        <span className="inline-block px-2 rounded bg-white border text-slate-300 ml-1">&lt;2</span>
      </p>
    </div>
  );
});
ScoringMatrix.displayName = 'ScoringMatrix';


/**
 * ScoringBacktest — W50-style walk-forward backtest over the last 50 draws
 * comparing the OLD Scoring Matrix formula vs the NEW one (Same-Date ×2 + R25 ×2).
 */
const ScoringBacktest = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const res = await axios.get(`${API}/analysis/w6-scoring-backtest`);
      setResult(res.data);
    } catch (e) {
      toast.error('Backtest failed — please retry');
    } finally {
      setRunning(false);
    }
  };

  const renderMetricRow = (label, mkey, res) => {
    const o = res.old[mkey];
    const n = res.new[mkey];
    const g = res.nogap[mkey];
    const oPct = res.old[`${mkey}_pct`];
    const nPct = res.new[`${mkey}_pct`];
    const gPct = res.nogap[`${mkey}_pct`];
    const dNew = n - o;
    const dGap = g - n;
    const renderDelta = (v) => (
      <td className={`px-3 py-2 text-center font-black ${v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : 'text-slate-400'}`}>
        {v > 0 ? `+${v}` : v}
      </td>
    );
    return (
      <tr className="border-b border-slate-100" key={mkey}>
        <td className="px-3 py-2 font-bold text-slate-700">{label}</td>
        <td className="px-3 py-2 text-center font-mono">{o} <span className="text-slate-500">({oPct}%)</span></td>
        <td className="px-3 py-2 text-center font-mono font-bold">{n} <span className="text-slate-500">({nPct}%)</span></td>
        {renderDelta(dNew)}
        <td className="px-3 py-2 text-center font-mono font-bold bg-amber-50">{g} <span className="text-slate-500">({gPct}%)</span></td>
        {renderDelta(dGap)}
      </tr>
    );
  };

  return (
    <section className="bg-white border-2 border-violet-400 rounded-xl overflow-hidden shadow-sm mt-4" data-testid="w6-scoring-backtest">
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-3 text-white flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-extrabold text-base sm:text-lg">🧪 Formula Backtest — Last 50 Draws (Walk-Forward)</h3>
          <p className="text-[11px] text-white/85 mt-1">
            OLD = Same-Date ×1, no R25 &nbsp;|&nbsp; NEW = Same-Date ×2 + R25 ×2 &nbsp;|&nbsp; <b>NEW w/o Gap</b> = NEW with GapRatio removed (×0). Each draw scored using only prior draws.
          </p>
        </div>
        <Button onClick={run} disabled={running} data-testid="w6-backtest-run-btn"
          className="bg-white text-violet-700 hover:bg-violet-100 font-bold">
          {running ? (<><CircleNotch size={16} className="animate-spin mr-1" /> Running…</>) : (result ? 'Re-run Backtest' : 'Run Backtest')}
        </Button>
      </div>

      {result && !result.error && (
        <div className="p-4 space-y-4" data-testid="w6-backtest-results">
          <div className={`rounded-lg px-4 py-3 font-bold text-sm ${result.delta_nogap.top10 > 0 ? 'bg-emerald-50 text-emerald-800 border border-emerald-300' : result.delta_nogap.top10 < 0 ? 'bg-red-50 text-red-800 border border-red-300' : 'bg-slate-50 text-slate-700 border border-slate-300'}`}
            data-testid="w6-backtest-verdict">
            {result.delta_nogap.top10 > 0 ? '✅ ' : result.delta_nogap.top10 < 0 ? '⚠️ ' : '➖ '}{result.verdict}
            <span className="font-normal text-xs ml-2 text-slate-500">
              (draws #{result.first_test_draw} → #{result.last_test_draw}, {result.max_hits} winning balls total)
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="w6-backtest-summary-table">
              <thead>
                <tr className="bg-violet-50 border-b-2 border-violet-200">
                  <th className="px-3 py-2 text-left font-black text-slate-800">Metric (hits of {result.max_hits})</th>
                  <th className="px-3 py-2 text-center font-black text-slate-600">OLD</th>
                  <th className="px-3 py-2 text-center font-black text-violet-700">NEW</th>
                  <th className="px-3 py-2 text-center font-black text-slate-800">Δ vs OLD</th>
                  <th className="px-3 py-2 text-center font-black text-amber-700 bg-amber-50">NEW w/o Gap</th>
                  <th className="px-3 py-2 text-center font-black text-slate-800">Δ vs NEW</th>
                </tr>
              </thead>
              <tbody>
                {renderMetricRow("Σ Total — Top 5", "top5", result)}
                {renderMetricRow("Σ Total — Top 10", "top10", result)}
                {renderMetricRow("Σ Total — Top 15", "top15", result)}
                {renderMetricRow("Per-Ball Column — Top 5", "perball_top5", result)}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto max-h-[40vh] border border-slate-200 rounded">
            <table className="w-full text-[11px]" data-testid="w6-backtest-perdraw-table">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr className="border-b border-slate-300">
                  <th className="px-2 py-1.5 text-left font-bold">Draw</th>
                  <th className="px-2 py-1.5 text-left font-bold">Date</th>
                  <th className="px-2 py-1.5 text-left font-bold">Winners</th>
                  <th className="px-2 py-1.5 text-center font-bold text-slate-600">OLD top-10</th>
                  <th className="px-2 py-1.5 text-center font-bold text-violet-700">NEW top-10</th>
                  <th className="px-2 py-1.5 text-center font-bold text-amber-700">w/o Gap top-10</th>
                  <th className="px-2 py-1.5 text-center font-bold">Δ</th>
                  <th className="px-2 py-1.5 text-left font-bold">w/o-Gap ranks of winners</th>
                </tr>
              </thead>
              <tbody>
                {result.per_draw.map((r) => {
                  const d = r.nogap.top10 - r.new.top10;
                  return (
                    <tr key={r.draw_number} className="border-b border-slate-100 hover:bg-violet-50" data-testid={`w6-backtest-row-${r.draw_number}`}>
                      <td className="px-2 py-1 font-mono">{r.draw_number}</td>
                      <td className="px-2 py-1">{r.date}</td>
                      <td className="px-2 py-1 font-mono">{r.winners.join(', ')}</td>
                      <td className="px-2 py-1 text-center">{r.old.top10}/5</td>
                      <td className="px-2 py-1 text-center">{r.new.top10}/5</td>
                      <td className="px-2 py-1 text-center font-bold">{r.nogap.top10}/5</td>
                      <td className={`px-2 py-1 text-center font-black ${d > 0 ? 'text-emerald-600' : d < 0 ? 'text-red-600' : 'text-slate-300'}`}>{d > 0 ? `+${d}` : d}</td>
                      <td className="px-2 py-1 font-mono text-slate-500">{r.nogap_ranks.join(', ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};


/**
 * SelectionPool — 🎯 3 regime lists (Momentum / Month-Affinity / Same-Date, top 6 each)
 * whose union forms a ~13-16 number candidate pool for the next draw.
 * Includes walk-forward coverage backtest vs the blended top-15.
 */
const REGIME_STYLE = {
  momentum: { chip: 'bg-rose-100 text-rose-800 border-rose-300', dot: 'bg-rose-500', card: 'border-rose-300', head: 'from-rose-500 to-orange-500' },
  affinity: { chip: 'bg-blue-100 text-blue-800 border-blue-300', dot: 'bg-blue-500', card: 'border-blue-300', head: 'from-blue-500 to-cyan-500' },
  same_date: { chip: 'bg-violet-100 text-violet-800 border-violet-300', dot: 'bg-violet-500', card: 'border-violet-300', head: 'from-violet-500 to-purple-500' },
};

const SelectionPool = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/analysis/w6-selection-pool`);
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load Selection Pool');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="w6-pool-loading">
        <CircleNotch size={28} className="animate-spin text-emerald-600" />
        <span className="ml-2 text-sm text-slate-500">Computing regime lists + 50-draw coverage backtest…</span>
      </div>
    );
  }
  if (!data || data.error) return <p className="text-sm text-red-600 py-8">Selection Pool unavailable.</p>;

  const regimeKeys = ['momentum', 'affinity', 'same_date'];
  const bt = data.backtest || {};
  const poolBetter = (bt.pool_pct || 0) >= (bt.blended_top15_pct || 0);

  return (
    <div className="space-y-4" data-testid="w6-selection-pool">
      <section className="bg-white border-2 border-emerald-400 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 text-white flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-extrabold text-base sm:text-lg">🎯 Selection Pool — Next Draw: {data.next_draw?.date}</h3>
            <p className="text-[11px] text-white/85 mt-1">
              Union of top-{data.top_k} from 3 regimes. {data.recipe}
            </p>
          </div>
          <div className="text-right text-[11px] text-emerald-100" data-testid="w6-pool-coverage">
            <div>50-draw coverage: <b>{bt.pool_hits}/{bt.max_hits} ({bt.pool_pct}%)</b> — avg pool {bt.avg_pool_size} numbers</div>
            <div>vs blended Σ-Total top-15: {bt.blended_top15_hits}/{bt.max_hits} ({bt.blended_top15_pct}%) {poolBetter ? '✅ pool wins' : ''}</div>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {regimeKeys.map((rk) => {
            const reg = data.regimes?.[rk];
            const st = REGIME_STYLE[rk];
            if (!reg) return null;
            return (
              <div key={rk} className={`border-2 ${st.card} rounded-lg overflow-hidden`} data-testid={`w6-pool-${rk}`}>
                <div className={`bg-gradient-to-r ${st.head} text-white px-3 py-2`}>
                  <p className="font-black text-sm">{reg.label} — Top {data.top_k}</p>
                  <p className="text-[10px] text-white/85">{reg.description}</p>
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {reg.top.map((r, i) => (
                      <tr key={r.number} className="border-b border-slate-100">
                        <td className="px-3 py-1.5 text-slate-400 font-mono w-8">#{i + 1}</td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 font-black ${st.chip}`}>
                            {r.number}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-slate-600">{r.score.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        <div className="px-4 pb-4">
          <div className="border-2 border-emerald-300 rounded-lg p-4 bg-emerald-50/50" data-testid="w6-pool-union">
            <p className="font-black text-sm text-emerald-900 mb-3">
              Candidate Pool — {data.pool_size} numbers
              <span className="font-normal text-xs text-slate-500 ml-2">
                (dots = regime membership: <span className="inline-block w-2 h-2 rounded-full bg-rose-500" /> momentum
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 ml-1" /> affinity
                <span className="inline-block w-2 h-2 rounded-full bg-violet-500 ml-1" /> same-date)
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {data.pool.map((p) => (
                <div key={p.number} className="flex flex-col items-center" data-testid={`w6-pool-number-${p.number}`}>
                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full border-2 font-black text-sm ${p.tags.length > 1 ? 'bg-amber-100 text-amber-900 border-amber-400' : REGIME_STYLE[p.tags[0]]?.chip || 'bg-slate-100'}`}>
                    {p.number}
                  </span>
                  <div className="flex gap-0.5 mt-1">
                    {p.tags.map((t) => (
                      <span key={t} className={`w-1.5 h-1.5 rounded-full ${REGIME_STYLE[t]?.dot}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 italic mt-3">
              Amber chips belong to 2+ regimes (strongest signals). Suggested pick pattern: 2 from Momentum + 2 from Month-Affinity + 1 from Same-Date.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};


const W6_METHODS = [
  { key: 'diffpct', label: 'W6.1 — Month Diff% Rebound', desc: 'Historical% − 2026% largest gap (most underperforming vs own baseline).' },
  { key: 'oct_affinity', label: 'W6.2 — Month Affinity L4Y (Neg)', desc: 'Never hit this ball in {MONTH} across the last 4 years, AND negative 2026 diff%.' },
  { key: 'momentum', label: 'W6.3 — Recent Momentum (R25+R50)', desc: 'Currently in both R25 and R50 (any position), ranked by historical %.' },
];
const W6_METHOD_STYLE = {
  diffpct: { head: 'from-rose-600 to-orange-600', chip: 'bg-rose-100 text-rose-800 border-rose-300' },
  oct_affinity: { head: 'from-amber-600 to-yellow-600', chip: 'bg-amber-100 text-amber-800 border-amber-300' },
  momentum: { head: 'from-cyan-600 to-blue-600', chip: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
};
const W6_BANDS = [
  { key: 'T5', start: 0, end: 5 }, { key: 'T10', start: 5, end: 10 },
  { key: 'T15', start: 10, end: 15 }, { key: 'T20', start: 15, end: 20 },
];

// 3 independent prediction methodologies, each computed per ball (B1-B5).
// Mirrors backend walk-forward predictors `_predict_w6_1_diffpct` /
// `_predict_w6_2_oct_affinity` / `_predict_w6_3_recent_momentum`
// (accuracy.py) exactly, so W50's current picks for W6.1/W6.2/W6.3 match
// what this panel shows.
const rankDiffPct = (numbers) => [...numbers].sort((a, b) => (b.v2_pct_pre_2026 - b.v2_pct_2026) - (a.v2_pct_pre_2026 - a.v2_pct_2026));
const rankOctAffinity = (numbers) => numbers.filter(n => (n.v2_may_last4_count || 0) === 0 && (n.v2_diff_pct || 0) < 0).sort((a, b) => a.v2_diff_pct - b.v2_diff_pct);
const rankMomentum = (numbers) => numbers.filter(n => (n.recent_25 || 0) > 0 && (n.recent_50 || 0) > 0).sort((a, b) => b.v2_pct_pre_2026 - a.v2_pct_pre_2026);
const W6_RANKERS = { diffpct: rankDiffPct, oct_affinity: rankOctAffinity, momentum: rankMomentum };

const PredictionPanel = ({ data }) => {
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const monthLabel = data?.current_month_name || 'Month';
  return (
    <div className="space-y-6" data-testid="w6-prediction-panel">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-[11px] text-indigo-800">
        <b>3 independent prediction methodologies</b>, computed separately per ball (B1-B5). Each chip shows the Diff% (Historical% − 2026%) for context. Bands are EXCLUSIVE rank groups — T5 = ranks 1-5, T10 = ranks 6-10, T15 = ranks 11-15, T20 = ranks 16-20. W6.2 and W6.3 only surface numbers that genuinely qualify, so their lists may be shorter than 20 (by design — no backfill).
      </div>
      {ballKeys.map((bk, bi) => {
        const numbers = data?.balls?.[bk]?.numbers || [];
        return (
          <div key={bk} className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid={`w6-prediction-ball-${bi + 1}`}>
            <div className="bg-slate-800 text-white px-4 py-2 font-black text-sm">Ball {bi + 1}</div>
            <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
              {W6_METHODS.map((m) => {
                const list = W6_RANKERS[m.key](numbers);
                const st = W6_METHOD_STYLE[m.key];
                return (
                  <div key={m.key} className="p-3" data-testid={`w6-prediction-${bk}-${m.key}`}>
                    <div className={`bg-gradient-to-r ${st.head} text-white rounded px-2 py-1.5 mb-2`}>
                      <p className="font-black text-[11px]">{m.label}</p>
                      <p className="text-[9px] text-white/80 leading-tight mt-0.5">{m.desc.replace('{MONTH}', monthLabel)}</p>
                    </div>
                    {list.length === 0 ? (
                      <p className="text-[11px] text-slate-400 italic py-2">No qualifying numbers this ball.</p>
                    ) : (
                      W6_BANDS.map((band) => {
                        const slice = list.slice(band.start, band.end);
                        if (slice.length === 0) return null;
                        return (
                          <div key={band.key} className="mb-1.5 flex items-start gap-1">
                            <span className="text-[9px] font-black text-slate-400 uppercase mt-0.5 shrink-0">{band.key}:</span>
                            <span className="inline-flex flex-wrap gap-1">
                              {slice.map((n) => (
                                <span
                                  key={n.number}
                                  className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${st.chip}`}
                                  title={`#${n.number} · Diff% ${n.v2_diff_pct >= 0 ? '+' : ''}${n.v2_diff_pct?.toFixed(2)}%`}
                                  data-testid={`w6-prediction-chip-${bk}-${m.key}-${n.number}`}
                                >
                                  {n.number}
                                  <span className="text-[8px] opacity-70">{n.v2_diff_pct >= 0 ? '+' : ''}{n.v2_diff_pct?.toFixed(1)}%</span>
                                </span>
                              ))}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};


export default function Window6MonthlyFrequency() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'overall_pct', dir: 'desc' });

  const [colWidths, setColWidths] = useState({
    num: 40, pct: 55, mpct: 55, dpct: 55, yr: 45, total: 45, mn: 55, trend: 55
  });
  const handleResize = (key, w) => setColWidths(prev => ({ ...prev, [key]: w }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/monthly-frequency`);
      setData(res.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/monthly-frequency/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `monthly_frequency_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch { toast.error('Failed to export'); }
    finally { setExportLoading(false); }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 6</h1>
                <p className="text-xs text-gray-500">Monthly & Annual Number Frequency</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => { setColWidths({ num: 40, pct: 55, mpct: 55, dpct: 55, yr: 45, total: 45, mn: 55, trend: 55 }); toast.success('Reset'); }} variant="ghost" size="sm" className="text-gray-500"><ArrowsOutLineHorizontal size={18} /></Button>
              <Button onClick={handleExport} disabled={exportLoading} className="btn-secondary flex items-center gap-2" data-testid="export-btn">
                {exportLoading ? <CircleNotch size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <PageConsensusBanner className="mb-4" />
        <WindowNarrative windowId="W6" {...NARRATIVES.W6} className="mb-5" />
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Draws</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_draws}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Historical Years</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  {data.hist_years?.[0]}-{data.hist_years?.[data.hist_years.length - 1]}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-blue-50 border-blue-200">
                <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Current Period</p>
                <p className="text-2xl font-bold text-blue-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.current_month_name} {data.current_year}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Numbers Tracked</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>1-50</p>
                <p className="text-xs text-gray-400">Per ball position</p>
              </div>
            </div>

            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
              <ArrowsOutLineHorizontal size={14} />
              <span>Drag column borders to resize | <strong>%</strong> = overall frequency | <strong>{data.current_month_abbr}%</strong> = {data.current_month_name}-specific frequency | <strong>Diff%</strong> = Overall% - {data.current_month_abbr}% | <strong>Trend</strong> = last 3 years pattern</span>
            </div>

            <Tabs defaultValue="ball_1" className="space-y-4">
              <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
                {Object.entries(ballLabels).map(([key, label]) => (
                  <TabsTrigger key={key} value={key} data-testid={`w6-tab-${key}`} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold">
                    {label}
                  </TabsTrigger>
                ))}
                <TabsTrigger value="scoring" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white font-bold" data-testid="w6-tab-scoring">
                  🏏 Scoring Matrix
                </TabsTrigger>
                <TabsTrigger value="pool" className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white font-bold" data-testid="w6-tab-pool">
                  🎯 Selection Pool
                </TabsTrigger>
                <TabsTrigger value="prediction" className="data-[state=active]:bg-indigo-700 data-[state=active]:text-white font-bold" data-testid="w6-tab-prediction">
                  🔮 Prediction
                </TabsTrigger>
              </TabsList>

              {Object.entries(ballLabels).map(([key, label], idx) => {
                const ballObj = data.balls?.[key] || {};
                const numbersData = ballObj.numbers || ballObj || [];
                const w3Range = ballObj.w3_range || {};
                const currentBallNum = ballObj.current_number || 0;

                return (
                  <TabsContent key={key} value={key}>
                    {/* W3 Difference Range Banner */}
                    {w3Range.range_75_min_diff !== undefined && (
                      <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg p-3 mb-3 flex items-center gap-4 flex-wrap text-xs">
                        <span className="font-bold bg-white/20 px-2 py-1 rounded">W3 Range</span>
                        <span>Last {label}: <strong className="text-lg">{currentBallNum}</strong></span>
                        <span>75% diff: <strong>{w3Range.range_75_min_diff > 0 ? '+' : ''}{w3Range.range_75_min_diff}</strong> to <strong>{w3Range.range_75_max_diff > 0 ? '+' : ''}{w3Range.range_75_max_diff}</strong></span>
                        <span>Next range: <strong className="text-lg">{w3Range.range_75_min_num}</strong> to <strong className="text-lg">{w3Range.range_75_max_num}</strong></span>
                      </div>
                    )}
                    <YearOverdueSummary
                      summary={ballObj.year_overdue_summary}
                      years={data.summary_years}
                      currentYear={data.current_year}
                      ballIdx={idx + 1}
                    />
                    <div className="bg-white border border-gray-200 rounded overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
                          {label} — {data.current_month_name} Frequency by Year
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          How often each number (1-50) appeared in {label} position during {data.current_month_name} across years + {data.current_year} monthly breakdown
                        </p>
                      </div>
                      <BallTable
                        numbersData={numbersData}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        ballIdx={idx + 1}
                        monthAbbr={data.current_month_abbr || data.current_month_name}
                      />
                    </div>

                  </TabsContent>
                );
              })}

              {/* 🏏 Scoring Matrix — user's per-number, per-ball composite score.
                  Score = Historical% + 2026 Diff% + Jul Diff% + Jul@Other% +
                          Jul-Last4% + Gap Ratio (cur / avg).
                  Rows = numbers 1..50, columns = B1..B5 + Σ Total. */}
              <TabsContent value="scoring">
                <ScoringMatrix data={data} />
                <ScoringBacktest />
              </TabsContent>

              {/* 🎯 Selection Pool — 3 regime lists + union candidate pool */}
              <TabsContent value="pool">
                <SelectionPool />
              </TabsContent>

              {/* 🔮 Prediction — 3 independent methodologies (W6.1/6.2/6.3),
                  per ball, also tracked individually in W50. */}
              <TabsContent value="prediction">
                <PredictionPanel data={data} />
              </TabsContent>
            </Tabs>
          </>
        ) : null}
      </main>
    </div>
  );
}


export function Window6Content() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'overall_pct', dir: 'desc' });
  const [colWidths, setColWidths] = useState({ num: 40, pct: 55, mpct: 55, dpct: 55, yr: 45, total: 45, mn: 55, trend: 55 });
  const handleResize = (key, w) => setColWidths(prev => ({ ...prev, [key]: w }));

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/monthly-frequency`); setData(res.data); }
      catch { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded p-4">
          <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Draws</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{data.total_draws}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded p-4 bg-blue-50 border-blue-200">
          <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Current Period</p>
          <p className="text-2xl font-bold text-blue-700 mt-1">{data.current_month_name} {data.current_year}</p>
        </div>
      </div>

      <Tabs defaultValue="ball_1" className="space-y-4">
        <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
          {Object.entries(ballLabels).map(([key, label]) => (
            <TabsTrigger key={key} value={key} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold">{label}</TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(ballLabels).map(([key, label]) => {
          const ballObj = data.balls?.[key] || {};
          const numbersData = ballObj.numbers || ballObj || [];
          const w3Range = ballObj.w3_range || {};
          const currentBallNum = ballObj.current_number || 0;

          const sorted = [...(Array.isArray(numbersData) ? numbersData : [])].sort((a, b) => {
            let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
            return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
          });
          const topPredicted = new Set([...(Array.isArray(numbersData) ? numbersData : [])].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));

          return (
            <TabsContent key={key} value={key}>
              {w3Range.range_75_min_diff !== undefined && (
                <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg p-3 mb-3 flex items-center gap-4 flex-wrap text-xs">
                  <span className="font-bold bg-white/20 px-2 py-1 rounded">W3 Range</span>
                  <span>Last {label}: <strong className="text-lg">{currentBallNum}</strong></span>
                  <span>Next range: <strong className="text-lg">{w3Range.range_75_min_num}</strong> to <strong className="text-lg">{w3Range.range_75_max_num}</strong></span>
                </div>
              )}
              <div className="bg-white border border-gray-200 rounded overflow-hidden overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      {[['number','#'],['overall_pct','%'],['month_pct','M%'],['diff_pct','Diff'],['cy_count','CY'],['pred_score','Score'],['trend','Trend']].map(([k,l])=>(
                        <th key={k} className="px-2 py-2 text-[10px] uppercase text-gray-700 cursor-pointer hover:bg-gray-200 text-center" onClick={()=>handleSort(k)}>{l}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((n,idx) => (
                      <tr key={n.number} className={topPredicted.has(n.number)?'bg-blue-100':idx%2===0?'bg-white':'bg-gray-50'}>
                        <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                        <td className="px-2 py-1 border-b font-mono text-center">{n.overall_pct}%</td>
                        <td className="px-2 py-1 border-b font-mono text-center">{n.month_pct}%</td>
                        <td className={`px-2 py-1 border-b font-mono text-center ${n.diff_pct > 0 ? 'text-green-600' : n.diff_pct < 0 ? 'text-red-600' : ''}`}>{n.diff_pct}%</td>
                        <td className="px-2 py-1 border-b font-mono text-center">{n.cy_count}</td>
                        <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.pred_score}</td>
                        <td className="px-2 py-1 border-b text-center"><TrendBadge trend={n.trend} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
                <h3 className="font-bold mb-3">Top 10 — {label}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[...(Array.isArray(numbersData)?numbersData:[])].sort((a,b)=>(b.pred_score||0)-(a.pred_score||0)).slice(0,10).map((n,idx)=>(
                    <div key={n.number} className={`p-3 rounded-lg ${idx===0?'bg-sky-100 text-sky-900':'bg-white/10'}`}>
                      <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                      <div className="text-xs text-center mt-1 opacity-80">{n.overall_pct}% | M:{n.month_pct}%</div>
                      <div className="text-center font-bold mt-1">{n.pred_score}</div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </>
  );
}
